import { useLocation } from "react-router-dom";
import {
  entriesForPlacementOnPath,
  entryHref,
  type InboundEntry,
  type InboundPlacement,
} from "@/lib/shrinkdailyInbound";

interface Props {
  placement: InboundPlacement;
  /**
   * When set, forces the component to look up entries for this exact path
   * rather than the current route. Used by the Footer, which renders on
   * every page but only wants to emit the homepage-scoped entry when the
   * reader is on `/`.
   */
  pathOverride?: string;
}

/**
 * Renders every ShrinkDaily inbound-contract entry that matches the given
 * placement and the current route. The site-specific note in the prompt
 * makes it explicit: "Both anchors are already voice-compliant and short.
 * Do not expand them into product pitches." So the rendered surface is
 * short and factual: the surrounding prose (as small context text) with
 * the entry anchor as the linked phrase. UTM comes from the contract, not
 * from `networkUrl()`, so the contract stays the single source of truth.
 */
export default function InboundReciprocalLink({
  placement,
  pathOverride,
}: Props) {
  const location = useLocation();
  const path = pathOverride ?? location.pathname;
  const entries = entriesForPlacementOnPath(path, placement);
  if (entries.length === 0) return null;

  if (placement === "footer-crosslink") {
    return (
      <div
        className="mt-4 pt-4 text-sm leading-relaxed"
        style={{
          borderTop: "1px solid hsl(var(--footer-border, var(--border)))",
          color: "hsl(var(--footer-fg, var(--muted-foreground)))",
        }}
        data-testid="inbound-footer-crosslink"
      >
        {entries.map((e) => (
          <FooterCrosslink key={e.id} entry={e} />
        ))}
      </div>
    );
  }

  if (placement === "see-also") {
    return (
      <aside
        className="mt-8 rounded-md border p-5"
        style={{
          borderColor: "hsl(var(--border))",
          background: "hsl(var(--muted) / 0.4)",
        }}
        data-testid="inbound-see-also"
      >
        <p
          className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          See also
        </p>
        {entries.map((e) => (
          <SeeAlso key={e.id} entry={e} />
        ))}
      </aside>
    );
  }

  // Generic fallback for placements the contract may add in the future.
  return (
    <p className="mt-6 text-sm" data-testid={`inbound-${placement}`}>
      {entries.map((e) => (
        <GenericLink key={e.id} entry={e} />
      ))}
    </p>
  );
}

function FooterCrosslink({ entry }: { entry: InboundEntry }) {
  return (
    <p key={entry.id} className="m-0">
      {entry.surroundingProse ? (
        <>
          <span>{entry.surroundingProse}</span>{" "}
        </>
      ) : null}
      <a
        href={entryHref(entry)}
        data-inbound-contract={`shrinkdaily/${entry.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold"
      >
        {entry.anchor}
      </a>
    </p>
  );
}

function SeeAlso({ entry }: { entry: InboundEntry }) {
  return (
    <p
      className="mt-2 text-base leading-relaxed m-0"
      style={{ color: "hsl(var(--foreground))" }}
    >
      {entry.surroundingProse ? (
        <>
          <span>{entry.surroundingProse}</span>{" "}
        </>
      ) : null}
      <a
        href={entryHref(entry)}
        data-inbound-contract={`shrinkdaily/${entry.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold hover:underline"
        style={{ color: "hsl(var(--brand-text))" }}
      >
        {entry.anchor}{" "}
        <span className="link-arrow" aria-hidden="true">
          &rarr;
        </span>
      </a>
    </p>
  );
}

function GenericLink({ entry }: { entry: InboundEntry }) {
  return (
    <a
      href={entryHref(entry)}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold hover:underline"
    >
      {entry.anchor}
    </a>
  );
}
